import { useRef } from "react";

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const MONO = "JetBrains Mono, monospace";

const DimH = ({ x1, x2, y, label }) => (
  <g>
    <line x1={x1} y1={y} x2={x2} y2={y} stroke="#475569" strokeWidth="1" />
    <line x1={x1} y1={y - 4} x2={x1} y2={y + 4} stroke="#475569" strokeWidth="1" />
    <line x1={x2} y1={y - 4} x2={x2} y2={y + 4} stroke="#475569" strokeWidth="1" />
    <text x={(x1 + x2) / 2} y={y + 15} textAnchor="middle" fontSize="10" fill="#94A3B8" fontFamily={MONO}>{label}</text>
  </g>
);

const DimV = ({ x, y1, y2, label }) => (
  <g>
    <line x1={x} y1={y1} x2={x} y2={y2} stroke="#475569" strokeWidth="1" />
    <line x1={x - 4} y1={y1} x2={x + 4} y2={y1} stroke="#475569" strokeWidth="1" />
    <line x1={x - 4} y1={y2} x2={x + 4} y2={y2} stroke="#475569" strokeWidth="1" />
    <text x={x + 9} y={(y1 + y2) / 2} textAnchor="middle" fontSize="10" fill="#94A3B8" fontFamily={MONO} transform={`rotate(-90 ${x + 9} ${(y1 + y2) / 2})`}>{label}</text>
  </g>
);

const Ticks = ({ x, yBottom, h }) => (
  <g>
    {[0, 25, 50, 75, 100].map((t) => {
      const y = yBottom - (t / 100) * h;
      return (
        <g key={t}>
          <line x1={x} y1={y} x2={x + 7} y2={y} stroke="#475569" strokeWidth="1" />
          <text x={x - 4} y={y + 3} textAnchor="end" fontSize="8.5" fill="#64748B" fontFamily={MONO}>{t}%</text>
        </g>
      );
    })}
  </g>
);

const Handle = ({ x, y, cursor, testid, onDown }) => (
  <g style={{ cursor }} onPointerDown={onDown} data-testid={testid}>
    <circle cx={x} cy={y} r="14" fill="transparent" />
    <circle cx={x} cy={y} r="11" fill="none" stroke="#F59E0B" strokeWidth="1" opacity="0.45" />
    <circle cx={x} cy={y} r="6.5" fill="#F59E0B" stroke="#0B0F17" strokeWidth="2" />
  </g>
);

const capsulePath = (cx, cy, rx, ry) => {
  const r = Math.min(rx, ry);
  const s = Math.max(rx, ry) - r;
  return `M ${cx - s} ${cy - r} H ${cx + s} A ${r} ${r} 0 0 1 ${cx + s} ${cy + r} H ${cx - s} A ${r} ${r} 0 0 1 ${cx - s} ${cy - r} Z`;
};

const ShapeOutline = ({ shape, cx, cy, rx, ry, fill, stroke, strokeWidth = 2, filter }) => {
  if (shape === "rectangular") {
    return <rect x={cx - rx} y={cy - ry} width={2 * rx} height={2 * ry} rx="6" fill={fill} stroke={stroke} strokeWidth={strokeWidth} filter={filter} />;
  }
  if (shape === "stadium") {
    return <path d={capsulePath(cx, cy, rx, ry)} fill={fill} stroke={stroke} strokeWidth={strokeWidth} filter={filter} />;
  }
  return <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill={fill} stroke={stroke} strokeWidth={strokeWidth} filter={filter} />;
};

const useDiagramDrag = (svgRef, W, H, onFillY, onResizeDelta) => {
  const dragInfo = useRef(null);
  const svgPoint = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * W, y: ((e.clientY - r.top) / r.height) * H };
  };
  const startResize = (key) => (e) => {
    e.stopPropagation();
    svgRef.current?.setPointerCapture?.(e.pointerId);
    dragInfo.current = { key, x: e.clientX, y: e.clientY };
  };
  const onPointerDown = (e) => {
    svgRef.current?.setPointerCapture?.(e.pointerId);
    onFillY(svgPoint(e).y);
  };
  const onPointerMove = (e) => {
    const d = dragInfo.current;
    if (d) {
      if (!(e.buttons & 1)) {
        dragInfo.current = null;
        return;
      }
      const r = svgRef.current.getBoundingClientRect();
      const dxU = (e.clientX - d.x) / (r.width / W);
      const dyU = (e.clientY - d.y) / (r.height / H);
      d.x = e.clientX;
      d.y = e.clientY;
      onResizeDelta(d.key, dxU, dyU);
      return;
    }
    if (e.buttons === 1) onFillY(svgPoint(e).y);
  };
  const onPointerUp = () => {
    dragInfo.current = null;
  };
  return { startResize, svgProps: { onPointerDown, onPointerMove, onPointerUp } };
};

const MIN_CM = 1;

const VerticalSVG = ({ shape, wCm, hCm, lengthCm, frac, color, overflow, labels, onFillFrac, onResizeDim }) => {
  const svgRef = useRef(null);
  const W = 470;
  const H = 445;
  const left = 90;
  const right = 230;
  const top = 70;
  const bottom = 350;
  const rx = (right - left) / 2;
  const ry = 13;
  const bodyH = bottom - top;
  const surfaceY = bottom - frac * bodyH;
  const tankPath = `M ${left} ${top} L ${right} ${top} L ${right} ${bottom} A ${rx} ${ry} 0 0 1 ${left} ${bottom} Z`;
  const stroke = overflow ? "#EF4444" : "#3B4B63";

  const icx = 385;
  const icy = 185;
  const maxHalf = 52;
  const insetScale = maxHalf / (Math.max(wCm, hCm) / 2);
  const hw = (wCm / 2) * insetScale;
  const hh = (hCm / 2) * insetScale;

  const { startResize, svgProps } = useDiagramDrag(
    svgRef,
    W,
    H,
    (y) => onFillFrac(clamp((bottom - y) / bodyH, 0, 1)),
    (key, dxU, dyU) => {
      if (key === "length") onResizeDim("length", Math.max(MIN_CM, lengthCm - dyU * (lengthCm / bodyH)));
      else if (key === "width") onResizeDim(shape === "circular" ? "diameter" : "width", Math.max(MIN_CM, wCm + dxU * (wCm / (right - left))));
      else if (key === "inset-width") onResizeDim("width", Math.max(MIN_CM, wCm + dxU * (wCm / (2 * hw))));
      else if (key === "inset-depth") onResizeDim("height", Math.max(MIN_CM, hCm + dyU * (hCm / (2 * hh))));
    }
  );

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${W} ${H}`}
      className="w-full max-w-[470px] mx-auto cursor-ns-resize select-none"
      style={{ touchAction: "none" }}
      data-testid="tank-diagram-svg"
      {...svgProps}
    >
      <defs>
        <linearGradient id="liqV" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.9" />
          <stop offset="100%" stopColor={color} stopOpacity="0.45" />
        </linearGradient>
        <clipPath id="clipV"><path d={tankPath} /></clipPath>
        <filter id="glowRedV" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="0" stdDeviation="7" floodColor="#EF4444" floodOpacity="0.9" />
        </filter>
      </defs>

      <path d={tankPath} fill="#0D131E" stroke={stroke} strokeWidth="2" filter={overflow ? "url(#glowRedV)" : undefined} />
      {frac > 0.001 && (
        <g clipPath="url(#clipV)">
          <rect x={left} y={surfaceY} width={right - left} height={bottom - surfaceY + ry} fill="url(#liqV)" />
          <ellipse cx={(left + right) / 2} cy={surfaceY} rx={rx} ry={ry * 0.65} fill={color} opacity="0.95" />
        </g>
      )}
      <ellipse cx={(left + right) / 2} cy={top} rx={rx} ry={ry} fill={frac >= 0.999 ? color : "#101725"} stroke={stroke} strokeWidth="2" />
      {overflow && (
        <text x={(left + right) / 2} y={top - ry - 12} textAnchor="middle" fontSize="11" fontWeight="700" fill="#EF4444" fontFamily={MONO} data-testid="overflow-warning">NEAR FULL</text>
      )}
      {frac > 0.001 && frac < 0.999 && (
        <g>
          <line x1={left} y1={surfaceY} x2={right + 6} y2={surfaceY} stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="5 4" />
          <text x={right + 10} y={surfaceY - 6} fontSize="10" fill="#F59E0B" fontFamily={MONO}>{labels.fill}</text>
        </g>
      )}
      <Ticks x={left - 12} yBottom={bottom} h={bodyH} />
      <DimV x={right + 84} y1={top} y2={bottom} label={labels.len} />
      <DimH x1={left} x2={right} y={bottom + ry + 16} label={labels.sideW} />

      {/* plan view (base shape) */}
      <text x={icx} y={icy - maxHalf - 12} textAnchor="middle" fontSize="9.5" fill="#64748B" fontFamily={MONO}>PLAN VIEW · BASE</text>
      <ShapeOutline shape={shape} cx={icx} cy={icy} rx={hw} ry={hh} fill="#101725" stroke="#3B4B63" />
      {frac > 0.001 && (
        <ShapeOutline shape={shape} cx={icx} cy={icy} rx={hw} ry={hh} fill={color} stroke="none" strokeWidth="0" filter={undefined} />
      )}
      <ShapeOutline shape={shape} cx={icx} cy={icy} rx={hw} ry={hh} fill="none" stroke="#3B4B63" />
      <text x={icx} y={icy + maxHalf + 18} textAnchor="middle" fontSize="10" fill="#94A3B8" fontFamily={MONO}>{labels.crossW}</text>

      {/* resize handles */}
      <Handle x={(left + right) / 2} y={top - 30} cursor="ns-resize" testid="resize-handle-length" onDown={startResize("length")} />
      <Handle x={right + 24} y={(top + bottom) / 2} cursor="ew-resize" testid={shape === "circular" ? "resize-handle-diameter" : "resize-handle-width"} onDown={startResize("width")} />
      {shape !== "circular" && (
        <>
          <Handle x={icx + hw} y={icy} cursor="ew-resize" testid="resize-handle-inset-width" onDown={startResize("inset-width")} />
          <Handle x={icx} y={icy + hh} cursor="ns-resize" testid="resize-handle-inset-depth" onDown={startResize("inset-depth")} />
        </>
      )}
    </svg>
  );
};

const HorizontalSVG = ({ shape, wCm, hCm, lengthCm, frac, color, overflow, labels, onFillFrac, onResizeDim }) => {
  const svgRef = useRef(null);
  const W = 480;
  const H = 400;
  const cx = 180;
  const cy = 185;
  const wD = shape === "stadium" ? Math.max(wCm, hCm) : wCm;
  const hD = shape === "stadium" ? Math.min(wCm, hCm) : hCm;
  const scale = 118 / (Math.max(wD, hD) / 2);
  const rx = (wD / 2) * scale;
  const ry = (hD / 2) * scale;
  const surfaceY = cy + ry - frac * 2 * ry;
  const t = clamp((cy - surfaceY) / ry, -1, 1);
  let halfChord;
  if (shape === "rectangular") halfChord = rx;
  else if (shape === "stadium") halfChord = rx - ry + ry * Math.sqrt(Math.max(0, 1 - t * t));
  else halfChord = rx * Math.sqrt(Math.max(0, 1 - t * t));
  const stroke = overflow ? "#EF4444" : "#3B4B63";
  const sx = cx + rx + 56;
  const sw = 84;
  const sy = cy - 46;
  const sh = 92;

  const { startResize, svgProps } = useDiagramDrag(
    svgRef,
    W,
    H,
    (y) => onFillFrac(clamp((cy + ry - y) / (2 * ry), 0, 1)),
    (key, dxU, dyU) => {
      if (key === "width") onResizeDim(shape === "circular" ? "diameter" : "width", Math.max(MIN_CM, wCm + dxU * (wCm / (2 * rx))));
      else if (key === "height") onResizeDim("height", Math.max(MIN_CM, hCm - dyU * (hCm / (2 * ry))));
      else if (key === "length") onResizeDim("length", Math.max(MIN_CM, lengthCm + dxU * (lengthCm / sw)));
    }
  );

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${W} ${H}`}
      className="w-full max-w-[480px] mx-auto cursor-ns-resize select-none"
      style={{ touchAction: "none" }}
      data-testid="tank-diagram-svg"
      {...svgProps}
    >
      <defs>
        <linearGradient id="liqH" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.9" />
          <stop offset="100%" stopColor={color} stopOpacity="0.45" />
        </linearGradient>
        <clipPath id="clipH">
          {shape === "rectangular" ? (
            <rect x={cx - rx} y={cy - ry} width={2 * rx} height={2 * ry} rx="6" />
          ) : shape === "stadium" ? (
            <path d={capsulePath(cx, cy, rx, ry)} />
          ) : (
            <ellipse cx={cx} cy={cy} rx={rx} ry={ry} />
          )}
        </clipPath>
        <filter id="glowRedH" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="0" stdDeviation="7" floodColor="#EF4444" floodOpacity="0.9" />
        </filter>
      </defs>

      {/* end view */}
      <ShapeOutline shape={shape} cx={cx} cy={cy} rx={rx} ry={ry} fill="#0D131E" stroke={stroke} filter={overflow ? "url(#glowRedH)" : undefined} />
      {frac > 0.001 && (
        <g clipPath="url(#clipH)">
          <rect x={cx - rx - 2} y={surfaceY} width={2 * rx + 4} height={cy + ry - surfaceY + 2} fill="url(#liqH)" />
        </g>
      )}
      {frac > 0.001 && frac < 0.999 && (
        <line x1={cx - halfChord} y1={surfaceY} x2={cx + halfChord} y2={surfaceY} stroke={color} strokeWidth="3" strokeLinecap="round" />
      )}
      {overflow && (
        <text x={cx} y={cy - ry - 14} textAnchor="middle" fontSize="11" fontWeight="700" fill="#EF4444" fontFamily={MONO} data-testid="overflow-warning">NEAR FULL</text>
      )}
      {frac > 0.001 && frac < 0.999 && (
        <g>
          <line x1={cx - halfChord} y1={surfaceY} x2={cx + rx + 12} y2={surfaceY} stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="5 4" />
          <text x={cx + rx + 16} y={surfaceY - 6} fontSize="10" fill="#F59E0B" fontFamily={MONO}>{labels.fill}</text>
        </g>
      )}
      <Ticks x={cx - rx - 12} yBottom={cy + ry} h={2 * ry} />
      <DimH x1={cx - rx} x2={cx + rx} y={cy + ry + 28} label={labels.crossW} />
      {labels.crossH && (
        <text x="8" y={cy + ry + 44} textAnchor="start" fontSize="10" fill="#94A3B8" fontFamily={MONO}>{labels.crossH}</text>
      )}

      {/* side view with length */}
      <rect x={sx} y={sy} width={sw} height={sh} fill="#0D131E" stroke="#3B4B63" strokeWidth="2" />
      {frac > 0.001 && (
        <rect x={sx} y={sy + sh - frac * sh} width={sw} height={frac * sh} fill={color} opacity="0.45" />
      )}
      <ellipse cx={sx + sw} cy={cy} rx="7" ry={sh / 2} fill="#101725" stroke="#3B4B63" strokeWidth="2" />
      <DimH x1={sx} x2={sx + sw} y={sy + sh + 24} label={labels.len} />

      {/* resize handles */}
      <Handle x={cx + rx + 2} y={cy} cursor="ew-resize" testid={shape === "circular" ? "resize-handle-diameter" : "resize-handle-width"} onDown={startResize("width")} />
      {shape !== "circular" && (
        <Handle x={cx} y={cy - ry - 2} cursor="ns-resize" testid="resize-handle-height" onDown={startResize("height")} />
      )}
      <Handle x={sx + sw + 16} y={cy} cursor="ew-resize" testid="resize-handle-length" onDown={startResize("length")} />
    </svg>
  );
};

export const TankDiagram = ({ orientation, shape, wCm, hCm, lengthCm, frac, color, overflow, labels, onFillFrac, onResizeDim }) => {
  if (orientation === "vertical") {
    return (
      <VerticalSVG shape={shape} wCm={wCm} hCm={hCm} lengthCm={lengthCm} frac={frac} color={color} overflow={overflow} labels={labels} onFillFrac={onFillFrac} onResizeDim={onResizeDim} />
    );
  }
  return (
    <HorizontalSVG shape={shape} wCm={wCm} hCm={hCm} lengthCm={lengthCm} frac={frac} color={color} overflow={overflow} labels={labels} onFillFrac={onFillFrac} onResizeDim={onResizeDim} />
  );
};
